import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

interface Highlight {
  icon: string;
  label: string;
  value: string;
}

interface HighlightsSectionProps {
  highlights: Highlight[];
}

export default function HighlightsSection({ highlights }: HighlightsSectionProps) {
  const { t } = useTranslation();
  const { ref: refHead, isVisible: visHead } = useScrollReveal();

  return (
    <section id="highlights" className="py-32 bg-[#FAFAF7]">
      <div className="max-w-7xl mx-auto px-8">
        {/* Heading */}
        <div
          ref={refHead}
          className={`text-center mb-20 reveal-up ${visHead ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="gold-line" />
            <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">
              {t('highlights.label')}
            </p>
            <div className="gold-line" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-[#1C1C1C]">
            {t('highlights.heading')}
          </h2>
          <p className="font-sans text-sm text-[#6B6460] mt-4 max-w-md mx-auto font-light">
            {t('highlights.sub')}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5DDD3]">
          {highlights.map((item, i) => (
            <HighlightCard key={item.label} item={item} index={i} />
          ))}
        </div>

        {/* Bottom note */}
        <div className="flex items-center justify-center gap-6 mt-16 pt-12 border-t border-[#E5DDD3]">
          <div className="text-center">
            <p className="font-serif text-3xl font-medium text-[#B59A6A]">AAA</p>
            <p className="font-sans text-[10px] tracking-widest uppercase text-[#6B6460] mt-1">
              {t('highlights.energyClass')}
            </p>
          </div>
          <div className="w-px h-10 bg-[#E5DDD3]" />
          <div className="text-center">
            <p className="font-serif text-3xl font-medium text-[#B59A6A]">2021</p>
            <p className="font-sans text-[10px] tracking-widest uppercase text-[#6B6460] mt-1">
              {t('highlights.yearBuilt')}
            </p>
          </div>
          <div className="w-px h-10 bg-[#E5DDD3]" />
          <div className="text-center">
            <p className="font-serif text-3xl font-medium text-[#B59A6A]">{t('highlights.availableValue')}</p>
            <p className="font-sans text-[10px] tracking-widest uppercase text-[#6B6460] mt-1">
              {t('highlights.available')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HighlightCard({ item, index }: { item: Highlight; index: number }) {
  const { ref, isVisible } = useScrollReveal();
  const { t } = useTranslation();
  const delays = ['reveal-delay-1','reveal-delay-2','reveal-delay-3','reveal-delay-4','reveal-delay-5','reveal-delay-6','reveal-delay-1','reveal-delay-2'];

  const displayValue = item.value.startsWith('prop.') ? t(item.value) : item.value;
  const displayLabel = item.label.startsWith('prop.') ? t(item.label) : item.label;

  return (
    <div ref={ref} className={`reveal-up ${delays[index % delays.length]} ${isVisible ? 'visible' : ''} bg-[#FAFAF7] p-10 group hover:bg-[#F5F0E8] transition-colors duration-400 text-center`}>
      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5">
        <i className={`${item.icon} text-2xl text-[#B59A6A]`} />
      </div>
      <p className="font-serif text-xl font-medium text-[#1C1C1C] mb-1 leading-tight">{displayValue}</p>
      <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460]">{displayLabel}</p>
    </div>
  );
}
